const MAX_LENGTH = 40
const MAX_WORDS = 4

function trimToLength(id: string, length: number): string {
    return id.slice(0, length).replace(/_+$/, '')
}

const FILLER_WORDS = new Set([
    'a', 'about', 'all', 'am', 'an', 'and', 'any', 'are', 'as', 'at', 'be', 'been', 'being', 'by', 'can', 'could',
    'did', 'do', 'does', 'each', 'every', 'for', 'from', 'had', 'has', 'have', 'how', 'i', 'if', 'in', 'into', 'is',
    'it', 'its', 'just', 'know', 'many', 'me', 'more', 'most', 'much', 'my', 'no', 'not', 'of', 'on', 'or', 'our',
    'ours', 'out', 'over', 'so', 'some', 'such', 'than', 'that', 'the', 'their', 'them', 'then', 'there', 'these',
    'they', 'this', 'those', 'to', 'up', 'us', 'very', 'was', 'we', 'well', 'were', 'what', 'when', 'where', 'whether',
    'which', 'who', 'whom', 'why', 'will', 'with', 'would', 'you', 'your', 'yours',
])

/**
 * Suggest a quiz item ID (question, dimension, band or report section) from its text.
 *
 * A non-empty result always satisfies the server's `WebValidationConstants.QuizIdPattern`
 * (`^[a-z][a-z0-9_]{1,39}\z`) and is unique against `takenIds`. Text with no letters or digits gives ''. It is only a suggestion the editor
 * fills in while the author hasn't typed an ID of their own; the save validator stays the judge.
 *
 * - Lowercases and strips accents, like `slugify`
 * - Long text (a question) drops filler words so the ID keeps the words that carry the meaning;
 *   short text (a name like "On track") keeps every word
 * - Keeps the first four words, joined by underscores, capped at 40 characters
 * - Prefixes `id_` when the text starts with a digit; pads a single letter
 * - Appends `_2`, `_3`… when the ID is already taken
 */
export function quizIdFromText(text: string | null | undefined, takenIds: Iterable<string>): string {
    const words = (text ?? '')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
    const significant = words.length > MAX_WORDS ? words.filter(word => !FILLER_WORDS.has(word)) : words
    let base = (significant.length > 0 ? significant : words).slice(0, MAX_WORDS).join('_')
    if (base === '') return ''
    if (/^[0-9]/.test(base)) base = `id_${base}`
    if (base.length < 2) base = `${base}_1`
    base = trimToLength(base, MAX_LENGTH)

    const taken = new Set(takenIds)
    if (!taken.has(base)) return base
    for (let suffix = 2; ; suffix++) {
        const tail = `_${suffix}`
        const candidate = `${trimToLength(base, MAX_LENGTH - tail.length)}${tail}`
        if (!taken.has(candidate)) return candidate
    }
}
